import { JiraServicedeskQueueAri } from '@atlassian/ari/jira-servicedesk/queue';
import { JiraSoftwareBoardAri } from '@atlassian/ari/jira-software/board';
import { JiraDashboardAri } from '@atlassian/ari/jira/dashboard';
import { JiraFilterAri } from '@atlassian/ari/jira/filter';
import { JiraPlanAri } from '@atlassian/ari/jira/plan';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import type {
	ChangeFavoriteMutationPayload,
	ChangeFavoritePayload,
	ItemType,
	ItemTypename,
} from '../../../model/types.tsx';

function assertUnreachable(_: never): never {
	throw new Error('Unexpected entity type');
}

const isSystemFilter = (filterId: string) => /^-\d+$/.test(filterId);

export const favoriteTypeToTypename = (
	type: ItemType,
	resourceId: string,
): ItemTypename | undefined => {
	switch (type) {
		case 'projects':
			return 'JiraProject';
		case 'filters':
			return isSystemFilter(resourceId) ? 'JiraSystemFilter' : 'JiraCustomFilter';
		case 'dashboards':
			return 'JiraDashboard';
		case 'plans':
			return 'JiraPlan';
		case 'boards':
			return 'JiraBoard';
		case 'queues':
			return 'JiraServiceManagementQueue';
		default:
			return undefined;
	}
};

const favoriteAriToId = (typename: ItemTypename, ari: string): string => {
	switch (typename) {
		case 'JiraProject':
			return JiraProjectAri.parse(ari).projectId;
		case 'JiraCustomFilter':
		case 'JiraSystemFilter':
			return JiraFilterAri.parse(ari).filterId;
		case 'JiraDashboard':
			return JiraDashboardAri.parse(ari).dashboardId;
		case 'JiraPlan':
			return JiraPlanAri.parse(ari).planId;
		case 'JiraBoard':
			return JiraSoftwareBoardAri.parse(ari).boardId;
		case 'JiraServiceManagementQueue':
			return JiraServicedeskQueueAri.parse(ari).queueId;
		default:
			assertUnreachable(typename);
	}
};

const favoriteTypenameToType = (typename: ItemTypename): ItemType => {
	switch (typename) {
		case 'JiraProject':
			return 'projects';
		case 'JiraCustomFilter':
		case 'JiraSystemFilter':
			return 'filters';
		case 'JiraDashboard':
			return 'dashboards';
		case 'JiraPlan':
			return 'plans';
		case 'JiraBoard':
			return 'boards';
		case 'JiraServiceManagementQueue':
			return 'queues';
		default:
			assertUnreachable(typename);
	}
};

/**
 * Map the provided mutation payload to the expected item shape required by the `FavoriteChangeContext`.
 * This primarily to support existing consumers of `FavoriteChangeContext` who may be dependent on the entity ID and
 * defined enum types. Ideally the context type and underlying consumers would leverage the entity ARI and GraphQL
 * typename so we can avoid this mapping, however that would require all consumers to have adopted Relay in their
 * experience which is an ongoing effort.
 */
export const favoriteMutationToRestPayload = ({
	id,
	typename,
	value,
	beforeEntity,
}: ChangeFavoriteMutationPayload): ChangeFavoritePayload => ({
	id: favoriteAriToId(typename, id),
	type: favoriteTypenameToType(typename),
	value,
	beforeEntity: beforeEntity
		? {
				id: favoriteAriToId(beforeEntity.typename, beforeEntity.id),
				type: favoriteTypenameToType(beforeEntity.typename),
			}
		: undefined,
});
